import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import {
  flatten,
  get,
  isFunction,
  matches,
  pullAt,
  values,
} from '@sama/micro-dash';
import { combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { withInsertedElements } from '../../../../shared/misc-utilities/misc-utils';
import { ContentConnector } from '../../../../team-apis/content/content-connector';
import { InFlightExtrapolationRequest } from '../../../../team-apis/content/in-flight-extrapolation-request';
import {
  FrameSpecificTag,
  PointType,
  ShapeLocation,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { sortedIndex } from '../../../to-replace/sorted-index';
import { ObjectWith } from '../../../types/misc';
import { ShapeState, TagsState } from '../state/shape-state';

export interface FrameItem {
  frame_number: number;
}

type ShapeWithInFlightMLERequests = ShapeState & {
  inFlightMLE: InFlightExtrapolationRequest[];
};

export function ensureKeyDynamicTag(
  shapeStore: Store<TagsState>,
  outputName: string,
  specifiedTag: FrameSpecificTag,
) {
  const tagStore = shapeStore('dynamic_tags')(outputName);
  const frameStore = ensureKeyframe(
    // @ts-ignore -- fix me
    tagStore,
    specifiedTag.frame_number,
    specifiedTag,
  );
  frameStore.set(specifiedTag);
}

export function toggleKeyframe<T extends FrameItem>(
  itemStore: Store<T[]>,
  frameNumber: number,
  ensureKeyframeCallback: () => void,
) {
  const items = itemStore.state();
  const insertPoint = findInsertPoint(items, frameNumber);
  const toggleOn = !isKeyLocation(items, frameNumber, insertPoint);

  if (toggleOn) {
    ensureKeyframeCallback();
  } else {
    removeKeyframe(itemStore, frameNumber);
  }

  return toggleOn;
}

export function removeKeyframe<T extends FrameItem>(
  itemStore: Store<T[]>,
  frameNumber: number,
) {
  const items = itemStore.state();
  const insertPoint = findInsertPoint(items, frameNumber);

  if (items.length === 1) {
    itemStore.delete();
  } else {
    itemStore.mutateUsing(pullAt, insertPoint);
  }
}

export function isShapeVisible$(
  frameNumberStore: Store<number>,
  shapeStore: Store<ShapeState>,
) {
  return combineLatest([
    shapeStore('key_locations').$.pipe(filter(isDefined)),
    frameNumberStore.$.pipe(filter(isDefined)),
  ]).pipe(
    map(([keyLocations, frameNumber]) => {
      const insertPoint = findInsertPoint(keyLocations, frameNumber);
      if (isKeyLocation(keyLocations, frameNumber, insertPoint)) {
        return keyLocations[insertPoint].visibility;
      }
      if (insertPoint > 0 && insertPoint < keyLocations.length) {
        return keyLocations[insertPoint - 1].visibility;
      } else {
        if (insertPoint === 0) {
          return 0;
        }
        return keyLocations[insertPoint - 1].visibility;
      }
    }),
  );
}

export function ensureKeyLocation(
  shapeStore: Store<ShapeState>,
  frameNumber: number,
  contentConnector: ContentConnector,
  specifiedLocation?: ShapeLocation<PointType>,
) {
  contentConnector.ensureKeyLocation(
    shapeStore.state().index,
    frameNumber,
    specifiedLocation,
  );

  const locationIndex = findKeyLocationIndex(
    shapeStore.state().key_locations,
    frameNumber,
  );

  return shapeStore('key_locations')(locationIndex);
}

export function isKeyLocation(
  items: FrameItem[],
  frameNumber: number,
  insertPoint = findInsertPoint(items, frameNumber),
) {
  return get(items, [insertPoint, 'frame_number']) === frameNumber;
}

export function isDynamicKeyframe(
  dynamicTags: ObjectWith<FrameSpecificTag[]>,
  frameNumber: number,
) {
  return values(dynamicTags).some((frameTags) =>
    frameTags.find(({ frame_number }) => frame_number === frameNumber),
  );
}

export function ensureKeyframe<T extends FrameItem>(
  itemStore: Store<T[]>,
  frameNumber: number,
  newItem: T | ((insertPoint: number) => T),
) {
  // fix me if you change this code
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
  const items = itemStore.state() || [];
  const insertPoint = findInsertPoint(items, frameNumber);
  const existingItem = items[insertPoint];
  // fix me if you change this code
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
  if (!existingItem || existingItem.frame_number !== frameNumber) {
    if (isFunction(newItem)) {
      // fix me if you change this code
      // eslint-disable-next-line no-param-reassign -- fix me!
      newItem = newItem(insertPoint);
    }
    itemStore.set(withInsertedElements(items, insertPoint, newItem));
  }
  return itemStore(insertPoint);
}

export function findKeyLocationIndex<T extends FrameItem>(
  items: T[],
  frameNumber: number,
) {
  return items.findIndex(matches({ frame_number: frameNumber }));
}

/**
 * Finds the index at which a frame item should be inserted to maintain
 * sortedness.
 */
export function findInsertPoint(items: FrameItem[], frameNumber: number) {
  return sortedIndex(
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    items?.map((item) => item.frame_number),
    frameNumber,
  );
}

export function removeKeyLocationIfNotLast(
  shapeStore: Store<ShapeState>,
  frameNumber: number,
) {
  if (shapeStore.state().key_locations.length > 1) {
    shapeStore('key_locations').mutateUsing((currentKeyLocations) =>
      pullAt(
        currentKeyLocations,
        findInsertPoint(currentKeyLocations, frameNumber),
      ),
    );
  }
}

/**
 * Returns:
 * - the frame item for the given frame number, if it exists
 * - else the closest item that is before the given frame number, if it exists
 * - else the first item (because the given frame number is before the first
 *   item)
 * This is used for labels to determine which key frame to pull a label's value
 * from.
 */
export function getEffectiveItem<T extends FrameItem>(
  items: T[],
  frameNumber: number,
): T | undefined {
  const index = findInsertPoint(items, frameNumber);
  const item = items[index];
  // fix me if you change this code
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
  if (item && item.frame_number === frameNumber) {
    return item;
  } else {
    return items[Math.max(index - 1, 0)];
  }
}

export function findClosestSmartSkipFrame(
  isDirectionForward: boolean,
  shapes: ShapeWithInFlightMLERequests[],
  maxFrameNumber: number,
  currentFrameNumber: number,
) {
  const frames = getImportantFrameNumbers(shapes);

  if (isDirectionForward) {
    return (
      frames.find(
        (frame) => frame > currentFrameNumber && frame <= maxFrameNumber,
      ) ?? maxFrameNumber
    );
  } else {
    const lastIndex = frames.findLastIndex(
      (frame) => frame < currentFrameNumber,
    );
    return lastIndex >= 0 ? frames[lastIndex] : 0;
  }
}

function getImportantFrameNumbers(shapes: ShapeWithInFlightMLERequests[]) {
  return flatten(
    shapes.map((shape) => [
      ...getFrameNumbersFromKeyLocations(shape),
      ...getFrameNumbersFromDynamicTags(shape),
      ...getFrameNumbersFromMLE(shape),
    ]),
  ).sort((a, b) => a - b);
}

function getFrameNumbersFromKeyLocations(shape: ShapeWithInFlightMLERequests) {
  return shape.key_locations.map((keyLocation) => keyLocation.frame_number);
}

function getFrameNumbersFromDynamicTags(shape: ShapeWithInFlightMLERequests) {
  return flatten(
    Object.values(shape.dynamic_tags).map((dynamicTags) =>
      // @ts-ignore -- fix me
      dynamicTags.map((tag) => tag.frame_number),
    ),
  );
}

function getFrameNumbersFromMLE(shape: ShapeWithInFlightMLERequests) {
  const frames = [];
  for (const keyLocation of shape.key_locations) {
    if (keyLocation.extrapolation) {
      const lastMLEFrame =
        keyLocation.frame_number + keyLocation.extrapolation.points.length + 1;
      frames.push(lastMLEFrame);
    }
  }

  if (shape.inFlightMLE.length) {
    const lastInFlightMLEFrames = shape.inFlightMLE.map(
      ({ keyFrameNumber, requestedExtrapolatedFramesCount }) =>
        keyFrameNumber + requestedExtrapolatedFramesCount + 1,
    );
    frames.push(...lastInFlightMLEFrames);
  }

  return frames;
}
